import { Stack } from '@mui/material';
import { useMessageContentTabIndex } from '@mui/x-chat/headless';
import { useRef } from 'react';

import { NotableMarkdown, isDefined } from 'common';

interface AgenticChatReasoningPreviewProps {
  text: string;
  presentation?: 'LivePreview' | 'History';
}

export function AgenticChatReasoningPreview(props: AgenticChatReasoningPreviewProps) {
  const { text, presentation } = props;
  const tabIndex = useMessageContentTabIndex();
  const scrollPositionRef = useRef<'Latest' | 'Paused'>('Latest');
  const previousContentRef = useRef<AgenticChatReasoningPreviewProps | undefined>(undefined);

  return (
    <Stack
      ref={(element: HTMLDivElement | null) => {
        if (
          !isDefined(element) ||
          (previousContentRef.current?.text === text &&
            previousContentRef.current?.presentation === presentation)
        ) {
          return;
        }

        previousContentRef.current = { text, presentation };
        if (presentation === 'LivePreview' && scrollPositionRef.current === 'Latest') {
          element.scrollTop = element.scrollHeight;
        }
      }}
      role="region"
      aria-label="Reasoning"
      tabIndex={tabIndex}
      onScroll={(event) => {
        const previewElement = event.currentTarget;
        scrollPositionRef.current =
          previewElement.scrollHeight - previewElement.scrollTop - previewElement.clientHeight <= 1
            ? 'Latest'
            : 'Paused';
      }}
      sx={{
        typography: 'monospace',
        fontStyle: 'italic',
        borderLeft: '2px solid',
        borderColor: 'divider',
        paddingTop: 2,
        paddingLeft: 2,
        maxHeight: '20lh',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        '& p': { margin: 0 },
        '& pre': { margin: 0, padding: 0, backgroundColor: 'transparent' },
      }}
    >
      <NotableMarkdown paragraphVariant="monospace" copyControls="Hidden" gap={0}>
        {text}
      </NotableMarkdown>
    </Stack>
  );
}
